/* The prototype's login page (split lime/white layout), backed by the real demo directory.
 *
 * The identity picker remains what it always was — a *selection*, not a login. The password
 * field is prototype theatre: it accepts the published demo password only, and nothing about
 * it is a credential (the server ignores everything but the picked identity, and only in
 * APP_ENV=local). When KBL's Entra tenant lands, the SSO button becomes the only path.
 */

import { useEffect, useMemo, useState } from 'react'

import { identity, setDemoIdentity } from '../api/client'
import type { DemoDirectory, DemoUser } from '../api/types'
import { errorMessage, initialsOf, useToast } from '../ui/chrome'
import { Icon } from '../ui/icons'

const DEMO_PASSWORD = 'kbl@2026'

const FEATURES: { icon: Parameters<typeof Icon>[0]['name']; title: string; desc: string }[] = [
  { icon: 'book', title: 'Knowledge Q&A', desc: 'Page-level citations on every answer' },
  { icon: 'doc', title: 'Smart Calculations', desc: 'Live leave, claim & attendance answers' },
  { icon: 'shield', title: 'Role-Aware Access', desc: 'Entra ID groups drive visibility' },
  { icon: 'activity', title: 'Guided Self-Service', desc: 'Leave, reimbursement, IT & onboarding' },
]

function roleDesc(person: DemoUser): string {
  if (person.is_admin) return 'Knowledge base, ingestion & review, notifications, analytics, audit'
  return 'The assistant, full screen — ask anything, get things done'
}

function roleLabel(person: DemoUser): string {
  return person.is_admin ? 'Admin' : 'Employee'
}

export default function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const toast = useToast()
  const [directory, setDirectory] = useState<DemoDirectory | null>(null)
  const [dirError, setDirError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState(DEMO_PASSWORD)
  const [emailErr, setEmailErr] = useState('')
  const [passErr, setPassErr] = useState('')
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState(false)
  const [ssoDetected, setSsoDetected] = useState(false)

  const people = useMemo(
    () => (directory ? [...directory.staff, ...directory.admins] : []),
    [directory],
  )

  useEffect(() => {
    identity
      .demoUsers()
      .then((dir) => {
        setDirectory(dir)
        const first = dir.staff[0] ?? dir.admins[0]
        if (first) {
          setSelected(first.upn)
          setEmail(first.upn)
        }
      })
      .catch((cause: unknown) => setDirError(errorMessage(cause)))
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setSsoDetected(true), 1400)
    return () => clearTimeout(timer)
  }, [])

  function pickRole(person: DemoUser) {
    setSelected(person.upn)
    setEmail(person.upn)
    setPassword(DEMO_PASSWORD)
    setEmailErr('')
    setPassErr('')
  }

  function signInAs(person: DemoUser) {
    setBusy(true)
    // The prototype simulates a ~1s token exchange; keep the pacing so the spinner reads.
    setTimeout(() => {
      setDemoIdentity({
        upn: person.upn,
        display_name: person.display_name,
        entra_group_ids: person.entra_group_ids,
      })
      toast(
        'ok',
        `Signed in as ${person.display_name}`,
        `${roleLabel(person)} · ${person.entra_group_ids.length} Entra ID group(s) resolved.`,
      )
      onSignedIn()
    }, 1000)
  }

  function doLogin(mode: 'sso' | 'form') {
    if (busy || people.length === 0) return

    if (mode === 'sso') {
      const person = people.find((p) => p.upn === selected) ?? people[0]
      if (person) signInAs(person)
      return
    }

    let ok = true
    const trimmed = email.trim().toLowerCase()
    if (!trimmed) {
      setEmailErr('Corporate email is required.')
      ok = false
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setEmailErr('Enter a valid email address.')
      ok = false
    }
    if (!password) {
      setPassErr('Password is required.')
      ok = false
    } else if (password.length < 6) {
      setPassErr('Password must be at least 6 characters.')
      ok = false
    }
    if (!ok) {
      toast('err', 'Sign-in failed', 'Correct the highlighted fields and try again.')
      return
    }

    const person = people.find((p) => p.upn.toLowerCase() === trimmed)
    if (!person) {
      setEmailErr('No KBL account found for this address.')
      toast('err', 'Account not found', 'Pick one of the demo identities above.')
      return
    }
    if (password !== DEMO_PASSWORD) {
      setPassErr(`Incorrect password. The demo password is ${DEMO_PASSWORD}.`)
      toast('err', 'Incorrect password', `Use ${DEMO_PASSWORD}.`)
      return
    }
    setSelected(person.upn)
    signInAs(person)
  }

  return (
    <div id="page-login" className="on">
      <div className="login-wrap">
        <div className="login-left">
          <div className="login-brand">
            <img src="/kbl-logo-mark.png" alt="Kirloskar Brothers Limited" />
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  color: '#fff',
                  lineHeight: 1.2,
                }}
              >
                Kirloskar Brothers Limited
              </div>
              <div style={{ fontSize: 11, color: '#8FA0B0' }}>
                Established 1888 · A Kirloskar Group Company
              </div>
            </div>
          </div>

          <div className="login-hero">
            <div className="eyebrow">Internal AI Assistant</div>
            <h1>From information access to intelligent action.</h1>
            <div className="login-feats">
              {FEATURES.map((f) => (
                <div key={f.title} className="login-feat">
                  <div className="lf-t">
                    <Icon name={f.icon} size={15} /> {f.title}
                  </div>
                  <div className="lf-d">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="login-foot">
            Helpline 1800-123-4443 &nbsp;·&nbsp; marketing@kbl.co.in
          </div>
        </div>

        <div className="login-right">
          <div className="login-card">
            <h2>Sign in</h2>
            <div className="lc-sub">
              Access is restricted to Kirloskar Brothers Limited employees on the KBL intranet
              network.
            </div>

            <div className="sso-detect" id="ssoDetect">
              {ssoDetected ? (
                <>
                  <span style={{ color: 'var(--ok)', display: 'flex' }}>
                    <Icon name="checkCircle" size={17} />
                  </span>
                  <span>
                    KBL intranet network detected — single sign-on pass-through is available.
                  </span>
                </>
              ) : (
                <>
                  <span className="spinner dark" />
                  <span>Detecting KBL intranet network…</span>
                </>
              )}
            </div>

            <button type="button" className="sso-btn" disabled={busy} onClick={() => doLogin('sso')}>
              <span className="ms-logo" aria-hidden="true">
                <i style={{ background: '#f25022' }} />
                <i style={{ background: '#7fba00' }} />
                <i style={{ background: '#00a4ef' }} />
                <i style={{ background: '#ffb900' }} />
              </span>
              Sign in with Microsoft Entra ID
            </button>

            <div className="divider">or choose a demo identity</div>

            <div className="role-list">
              {dirError && (
                <div className="alert a-danger">
                  <Icon name="alert" size={18} />
                  <div>
                    <strong>The demo directory could not be loaded.</strong>
                    {dirError}
                  </div>
                </div>
              )}
              {!directory && !dirError && (
                <div className="tiny muted" style={{ padding: '10px 2px' }}>
                  <span className="spinner dark" /> Loading the KBL demo directory…
                </div>
              )}
              {people.map((person) => (
                <button
                  key={person.upn}
                  type="button"
                  className={`role-opt${selected === person.upn ? ' on' : ''}`}
                  onClick={() => pickRole(person)}
                >
                  <span className="ra">{initialsOf(person.display_name)}</span>
                  <span style={{ minWidth: 0 }}>
                    <span className="rn">
                      {person.display_name} · {roleLabel(person)}
                    </span>
                    <br />
                    <span className="rr">{person.scenario_note ?? roleDesc(person)}</span>
                  </span>
                  <span className="rc">
                    <Icon name="checkCircle" size={18} />
                  </span>
                </button>
              ))}
            </div>

            <div className="field">
              <label htmlFor="loginEmail">
                Corporate email <span className="req">*</span>
              </label>
              <input
                id="loginEmail"
                type="email"
                autoComplete="username"
                className={`inp${emailErr ? ' err' : ''}`}
                placeholder="name@kbl.co.in"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                  setEmailErr('')
                }}
                onKeyDown={(event) => event.key === 'Enter' && doLogin('form')}
              />
              <div className={`err-msg${emailErr ? ' show' : ''}`}>
                <Icon name="alert" size={12} />
                <span>{emailErr}</span>
              </div>
            </div>

            <div className="field">
              <label htmlFor="loginPass">
                Password <span className="req">*</span>
              </label>
              <input
                id="loginPass"
                type="password"
                autoComplete="current-password"
                className={`inp${passErr ? ' err' : ''}`}
                placeholder="Enter your password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                  setPassErr('')
                }}
                onKeyDown={(event) => event.key === 'Enter' && doLogin('form')}
              />
              <div className={`err-msg${passErr ? ' show' : ''}`}>
                <Icon name="alert" size={12} />
                <span>{passErr}</span>
              </div>
              <div className="help">
                Demo password for every identity:{' '}
                <code style={{ background: '#eceee6', padding: '1px 5px', borderRadius: 2 }}>
                  {DEMO_PASSWORD}
                </code>
              </div>
            </div>

            <label className="chk">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => {
                  setRemember(event.target.checked)
                  toast(
                    'info',
                    event.target.checked ? 'Stay signed in enabled' : 'Stay signed in disabled',
                    '',
                  )
                }}
              />
              <span>Keep me signed in on this device</span>
            </label>

            <button
              type="button"
              className="btn btn-lg btn-block"
              style={{ marginTop: 10 }}
              disabled={busy || !directory}
              onClick={() => doLogin('form')}
            >
              {busy ? (
                <>
                  <span className="spinner" /> Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>

          </div>
        </div>
      </div>
    </div>
  )
}
